import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, query, where, orderBy, limit, startAfter } from "firebase/firestore";
import db from "./Firebase";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const PAGE_SIZE = 500;
const SERVER_FETCH = 700;

const formatTimestamp = (ts) => {
    if (!ts) return "N/A";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return isNaN(d.getTime()) ? "N/A" : d.toLocaleString();
};

const AnalyticsScreen = () => {
    const [userStats, setUserStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [namesLoading, setNamesLoading] = useState(false);
    const [expandedRows, setExpandedRows] = useState({});
    const [searchUserId, setSearchUserId] = useState("");
    const [lastDoc, setLastDoc] = useState(null);
    const [hasMore, setHasMore] = useState(false);

    const fetchNames = async (usersData) => {
        setNamesLoading(true);
        const nameMap = {};
        // Individual queries — most reliable approach
        await Promise.all(
            usersData.map(async (u) => {
                try {
                    const snap = await getDocs(
                        query(collection(db, "personal_details"), where("phone_no", "==", u.user_id))
                    );
                    if (!snap.empty) {
                        nameMap[u.user_id] = snap.docs[0].data().full_name?.trim() || "";
                    }
                } catch (_) { }
            })
        );
        setNamesLoading(false);
        return usersData.map((u) => ({ ...u, name: nameMap[u.user_id] || "" }));
    };

    const processSnapshot = (docs) => {
        const valid = [];
        for (const userDoc of docs) {
            const digits = userDoc.id.replace(/\D/g, "").slice(-10);
            if (digits.length !== 10) continue;
            const d = userDoc.data();
            const lastActiveRaw = d.last_active
                ? (d.last_active?.toDate ? d.last_active.toDate() : new Date(d.last_active))
                : null;
            valid.push({
                user_id: digits,
                name: "",
                total_opens: d.total_opens || 0,
                last_active: lastActiveRaw ? formatTimestamp(lastActiveRaw) : "N/A",
                last_active_raw: lastActiveRaw,
                is_active: d.is_active || false,
                is_inquisitive: d.is_inquisitive || false,
                page_visits: Array.isArray(d.page_visits) ? d.page_visits : [],
            });
            if (valid.length === PAGE_SIZE) break;
        }
        return valid;
    };

    const loadInitial = useCallback(async () => {
        setLoading(true);
        try {
            const q = query(
                collection(db, "users_activity"),
                orderBy("last_active", "desc"),
                limit(SERVER_FETCH)
            );
            const snap = await getDocs(q);
            const usersData = processSnapshot(snap.docs);
            setLastDoc(snap.docs[snap.docs.length - 1] || null);
            setHasMore(snap.docs.length >= SERVER_FETCH);
            const withNames = await fetchNames(usersData);
            setUserStats(withNames.filter((u) => u.name));
            setLoading(false);
        } catch (e) {
            console.error("Error loading analytics:", e);
            setLoading(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadInitial();
    }, [loadInitial]);

    const loadMore = async () => {
        if (!lastDoc || loadingMore) return;
        setLoadingMore(true);
        try {
            const q = query(
                collection(db, "users_activity"),
                orderBy("last_active", "desc"),
                startAfter(lastDoc),
                limit(SERVER_FETCH)
            );
            const snap = await getDocs(q);
            const newUsers = processSnapshot(snap.docs);
            setLastDoc(snap.docs[snap.docs.length - 1] || null);
            setHasMore(snap.docs.length >= SERVER_FETCH);
            const withNames = await fetchNames(newUsers);
            setUserStats((prev) => [...prev, ...withNames.filter((u) => u.name)]);
            setLoadingMore(false);
        } catch (e) {
            console.error("Error loading more:", e);
            setLoadingMore(false);
        }
    };

    const toggleRow = (userId) => {
        setExpandedRows((prev) => ({ ...prev, [userId]: !prev[userId] }));
    };

    const handleExport = () => {
        const rows = filteredUsers.map((u, i) => ({
            "Sr.": i + 1,
            "Phone No": u.user_id,
            "Name": u.name || "",
            "Total Opens": u.total_opens,
            "Last Active": u.last_active,
            "Active (45s+)": u.is_active ? "Yes" : "No",
            "Inquisitive (2+ Pages)": u.is_inquisitive ? "Yes" : "No",
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Analytics");
        const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const now = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
        saveAs(
            new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
            `UserAnalytics_${now}.xlsx`
        );
    };

    const filteredUsers = userStats.filter((user) =>
        user.name &&
        (user.user_id.includes(searchUserId) ||
        user.name.toLowerCase().includes(searchUserId.toLowerCase()))
    );

    return (
        <>
            <div className="p-2 flex justify-between">
                <Link to="/">
                    <img src="/images/menubutton.png" className="menu-toggle" alt="Menu" style={{ height: "2rem" }} />
                </Link>
            </div>

            <div className="card-header pb-0 px-3 d-flex justify-content-end">
                <Link to="/dashbaordall">
                    <button className="btn btn-success btn-sm mr-3">Log Dashboard</button>
                </Link>
                <Link to="/page_visit">
                    <button className="btn btn-success btn-sm">User Page Visits Logs</button>
                </Link>
            </div>

            <div className="p-4 bg-white shadow-lg rounded-lg">
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <h3 className="text-2xl font-bold mb-0">User Analytics</h3>
                    {/* <span style={{ fontSize: "13px", color: "#6b7280" }}>
                        {loading ? "Loading…" : `Showing ${filteredUsers.length} users`}
                        {namesLoading && <span className="ms-2 text-warning">· Loading names…</span>}
                    </span> */}
                </div>

                <div className="d-flex align-items-center gap-2 mb-4">
                    <input
                        type="text"
                        placeholder="Search by phone or name…"
                        value={searchUserId}
                        onChange={(e) => setSearchUserId(e.target.value)}
                        className="border p-2 rounded-md"
                        style={{ width: "280px" }}
                    />
                    <button
                        className="btn btn-success btn-sm"
                        onClick={handleExport}
                        disabled={loading || filteredUsers.length === 0}
                        style={{ marginTop: "1rem" }}
                    >
                        Export Excel
                    </button>
                    {/* <span style={{ fontSize: "13px", color: "#6b7280" }}>
                        {loading ? "Loading…" : `${filteredUsers.length} users`}
                        {namesLoading && <span className="ms-2 text-warning">· Loading names…</span>}
                    </span> */}
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-200">
                        <thead className="bg-gray-100">
                            <tr className="text-left">
                                <th className="border p-3 font-semibold">#</th>
                                <th className="border p-3 font-semibold">Phone No</th>
                                <th className="border p-3 font-semibold">Name</th>
                                <th className="border p-3 font-semibold">Total Opens</th>
                                <th className="border p-3 font-semibold">Last Active</th>
                                <th className="border p-3 font-semibold">Active (45s+)</th>
                                <th className="border p-3 font-semibold">Inquisitive (2+ Pages)</th>
                                <th className="border p-3 font-semibold">Page Visits</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="8" className="text-center p-5">
                                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                                        <span className="text-muted">Loading users & names, please wait…</span>
                                    </td>
                                </tr>
                            ) : filteredUsers.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center text-gray-500 p-4">
                                        No matching users found
                                    </td>
                                </tr>
                            ) : (
                                filteredUsers.map((user, idx) => (
                                    <React.Fragment key={user.user_id}>
                                        <tr className="hover:bg-gray-50">
                                            <td className="border p-3 text-gray-400" style={{ fontSize: "12px" }}>{idx + 1}</td>
                                            <td className="border p-3 font-medium">{user.user_id}</td>
                                            <td className="border p-3">
                                                {user.name
                                                    ? user.name
                                                    : namesLoading
                                                        ? <span className="text-muted" style={{ fontSize: "12px" }}>…</span>
                                                        : <span className="text-gray-400" style={{ fontSize: "12px" }}>—</span>
                                                }
                                            </td>
                                            <td className="border p-3">{user.total_opens}</td>
                                            <td className="border p-3">{user.last_active}</td>
                                            <td className={`border p-3 ${user.is_active ? "text-green-600" : "text-red-600"}`}>
                                                {user.is_active ? "🟢 Yes" : "🔴 No"}
                                            </td>
                                            <td className={`border p-3 ${user.is_inquisitive ? "text-green-600" : "text-red-600"}`}>
                                                {user.is_inquisitive ? "🟢 Yes" : "🔴 No"}
                                            </td>
                                            <td className="border p-3">
                                                <button
                                                    onClick={() => toggleRow(user.user_id)}
                                                    className="text-blue-600 font-medium flex items-center"
                                                >
                                                    {expandedRows[user.user_id] ? "▼ Hide" : "▶ View Visits"}
                                                </button>
                                            </td>
                                        </tr>
                                        {expandedRows[user.user_id] && (
                                            <tr>
                                                <td colSpan={8} className="border p-3 bg-gray-50">
                                                    {user.page_visits.length > 0 ? (
                                                        <table className="w-full border-collapse border border-gray-300 mt-2">
                                                            <thead className="bg-gray-200">
                                                                <tr>
                                                                    <th className="border p-2 text-left font-semibold">Module</th>
                                                                    <th className="border p-2 text-left font-semibold">Category</th>
                                                                    <th className="border p-2 text-left font-semibold">Description</th>
                                                                    <th className="border p-2 text-left font-semibold">Visit Time</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {user.page_visits
                                                                    .map((visit) => ({
                                                                        ...visit,
                                                                        visit_time: visit.visit_time?.toDate
                                                                            ? visit.visit_time.toDate()
                                                                            : new Date(visit.visit_time),
                                                                    }))
                                                                    .sort((a, b) => b.visit_time.getTime() - a.visit_time.getTime())
                                                                    .map((visit, i) => (
                                                                        <tr key={i} className="hover:bg-gray-100">
                                                                            <td className="border p-2">{visit.page_name}</td>
                                                                            <td className="border p-2">{visit.category}</td>
                                                                            <td className="border p-2">{visit.description}</td>
                                                                            <td className="border p-2">{formatTimestamp(visit.visit_time)}</td>
                                                                        </tr>
                                                                    ))}
                                                            </tbody>
                                                        </table>
                                                    ) : (
                                                        <span className="text-gray-500">No visits recorded</span>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Load More */}
                {!loading && hasMore && (
                    <div className="text-center mt-4">
                        <button
                            className="btn btn-outline-primary px-5"
                            onClick={loadMore}
                            disabled={loadingMore}
                        >
                            {loadingMore
                                ? <><span className="spinner-border spinner-border-sm me-2" role="status" />Loading…</>
                                : "Load More"
                            }
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

export default AnalyticsScreen;
